import { makeVar } from '@apollo/client';

import { IGroup } from 'types';

/**
 * Command-palette (Raycast/Spotlight) client state — a dedicated reactive var,
 * intentionally SEPARATE from `popupState`. `showPopup` calls `hideOtherPopUps`
 * which resets the whole switchboard; sharing that var would fight the palette's
 * own «pick entity → run action» flow. The palette always `close()`s before it
 * dispatches a `showPopup`, so the two portal overlays never stack.
 */
export type PaletteMode = 'search' | 'entityActions' | 'help';

export type PaletteEntityKind =
  | 'student'
  | 'group'
  | 'course'
  | 'teacher'
  | 'user'
  | 'lead';

/**
 * Minimal agreement projection carried on a student result — enough to drive the
 * «edit agreement» and «move to group» actions without re-fetching. Structurally
 * compatible with `MoveToGroupInlinePopup`'s `IAgreementForMove`.
 */
export interface IPaletteAgreement {
  id: string;
  currency?: string | null;
  agreementTitle?: string | null;
  group?: { id: string; name: string } | null;
}

export interface IPaletteStudentEntity {
  kind: 'student';
  id: string;
  title: string;
  subtitle?: string;
  isLmsBlocked: boolean;
  /** First (most-recent) agreement — drives edit-agreement / move-to-group. */
  agreement: IPaletteAgreement | null;
}

export interface IPaletteGroupEntity {
  kind: 'group';
  id: string;
  title: string;
  subtitle?: string;
  publishedLms: boolean;
  /** Adapted `IGroup` shape the LMS publish/unpublish popups expect. */
  group: IGroup;
}

export interface IPaletteCourseEntity {
  kind: 'course';
  id: string;
  title: string;
  subtitle?: string;
}

export interface IPaletteTeacherEntity {
  kind: 'teacher';
  id: string;
  title: string;
  subtitle?: string;
}

export interface IPaletteUserEntity {
  kind: 'user';
  id: string;
  title: string;
  subtitle?: string;
}

/** Задача #116: лид Kommo — открывается карточкой в разделе «Лиды». */
export interface IPaletteLeadEntity {
  kind: 'lead';
  id: string;
  title: string;
  subtitle?: string;
}

export type PaletteEntity =
  | IPaletteStudentEntity
  | IPaletteGroupEntity
  | IPaletteCourseEntity
  | IPaletteTeacherEntity
  | IPaletteUserEntity
  | IPaletteLeadEntity;

export interface IPaletteState {
  open: boolean;
  query: string;
  mode: PaletteMode;
  /** The entity whose actions are being drilled into (`entityActions` mode). */
  activeEntity: PaletteEntity | null;
  /** Flat cursor across the visible rows (group headers are skipped). */
  selectedIndex: number;
}

export const defaultPaletteState: IPaletteState = Object.freeze({
  open: false,
  query: '',
  mode: 'search',
  activeEntity: null,
  selectedIndex: 0,
});

export const paletteState = makeVar<IPaletteState>({ ...defaultPaletteState });
